/**
 * Authentication context and provider.
 *
 * Provides authentication state (current user, roles helper) to the React
 * component tree. Replaces the AngularJS $rootScope.userProfile / $auth
 * service pattern.
 *
 * System info (/api/info) is owned by AppInfoContext; this provider consumes
 * it via useAppInfo() for hasRole() and to refresh info after login, since
 * the available roles list can change once a user authenticates.
 *
 * Auth state is determined by hitting GET /api/profiles/self on mount.
 * Since M1.7, auth is a brewauth-issued Bearer held in memory by
 * oidc-client-ts (there is no session cookie) — apiClient attaches it to the
 * probe. A 200 with a profile body means logged in; a 401 means
 * logged out; anything else means the backend is unavailable.
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import apiClient from '@/api/client';
import { useAppInfo } from '@/app/useAppInfo';
import {
  auth,
  getAccessToken,
  reauthContextFrom,
  setRemoteSignoutHandler,
  logout as oidcLogout,
  loginFresh,
  userManager,
} from './reauthClient';
import { startActivityTracking } from '@brew/ui/auth';
import { clearUserStorage } from './clearUserStorage';
import { takeCappedAttempt } from './attemptCap';
import {
  LOCAL_SIGNOUT_KEY,
  authTimeOf,
  isLocalSignoutPending,
  markLocalSignoutPending,
} from './localSignoutMarker';
import type { UserProfile } from '@/types';
import { AuthContext, type AuthContextType } from './authContextObject';

// Retrying a pending sign-out's end_session on load (#2272): once per page
// load, and like autoLogin at most 3 per 120 s per tab, so a brewauth that
// keeps failing cannot turn it into a redirect loop. A started redirect
// never settles (the page is leaving), so wait at most this long for it.
const SIGNOUT_RETRY_KEY = 'brewlytics.signoutRetry';
const SIGNOUT_RETRY_MAX = 3;
const SIGNOUT_RETRY_WINDOW_MS = 120_000;
const SIGNOUT_RETRY_WAIT_MS = 5_000;
let signoutRetriedThisLoad = false;


export function AuthProvider({ children }: Readonly<{ children: React.ReactNode }>) {
  const { info } = useAppInfo();
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [authenticated, setAuthenticated] = useState(false);
  const [remotelySignedOut, setRemotelySignedOut] = useState(false);
  const [localSignout, setLocalSignout] = useState(isLocalSignoutPending);
  const [unavailable, setUnavailable] = useState(false);
  // A profile has been loaded (and not since signed out): an unavailable
  // answer then must not tear down the user's page (jorigin/brewauth#139).
  const profileKnown = useRef(false);

  /**
   * Fetch current user's profile.
   *
   * A 200 response carrying a real profile body is the only signal we accept
   * as "logged in". An unauthenticated caller gets a 401
   * (`WWW-Authenticate: Bearer realm="brew"`, `X-Auth-Status: MISSING`); that
   * is the only answer that means "logged out". Everything else — a 5xx, a
   * network error, a 200 whose body is not a profile (it used to be an HTML
   * login page, which is why the body shape is checked, jorigin/brewauth#99) —
   * means the backend is unavailable, NOT that the user is signed out:
   * treating it as signed out sent ProtectedRoute to login() while the SSO
   * session was alive, minting a login per reload in a loop (jorigin/brewauth#139).
   *
   * We bypass the global 401 recovery ladder here because this probe
   * doubles as the source of truth for auth state; the redirect to /login
   * is driven by the ProtectedRoute, not by this fetch.
   */
  /** Only a 401 signs out. Anything else keeps a known profile; with none yet, it is "unavailable". */
  const failProfile = useCallback((status: number | undefined): false => { // NOSONAR typescript:S3516 published-contract: refreshProfile() returns failProfile(...) as its Promise<boolean> result, so the constant false is the answer it gives callers
    if (status !== 401 && profileKnown.current) return false;
    profileKnown.current = false;
    setUserProfile(null);
    setAuthenticated(false);
    setUnavailable(status !== 401);
    return false;
  }, []);

  const refreshProfile = useCallback(async (): Promise<boolean> => {
    const probe = () =>
      apiClient.get<unknown>('api/profiles/self', {
        skipAuthRedirect: true,
        headers: { Accept: 'application/json' },
      });
    const statusOf = (err: unknown) => (err as { response?: { status?: number } } | null)?.response?.status;
    try {
      // The probe skips @brew/ui's interceptor, so it lacks its rotation replay:
      // if the token it was sent with rotated mid-flight (another tab or the
      // renewal timer; rotation deletes the old one) the 401 is stale, not a
      // sign-out. Retry once with the new token (jorigin/brewauth#139).
      const sentWith = await getAccessToken().catch(() => null);
      const retryIfRotated = async (): Promise<boolean> => {
        const now = await getAccessToken().catch(() => null);
        return !!now && now !== sentWith;
      };
      let res = await probe().catch((err: unknown) => {
        if (statusOf(err) === 401) return { rejected: err };
        throw err;
      });
      if ('rejected' in res || res.status === 401) {
        if (!(await retryIfRotated())) {
          if ('rejected' in res) throw res.rejected;
        } else {
          res = await probe();
        }
      }
      const data = res.data;
      const looksLikeProfile =
        res.status === 200 &&
        !!data &&
        typeof data === 'object' &&
        '_id' in data;
      if (looksLikeProfile) {
        setUserProfile(data as UserProfile);
        setAuthenticated(true);
        setUnavailable(false);
        profileKnown.current = true;
        // Feed the re-auth modal the session's method + email from the access token
        // (createReauthClient's construction-time auto-populate can race login).
        void auth
          .decodeTokenPayload()
          .then((c) => auth.reauth.setContext(reauthContextFrom(c)));
        return true;
      }
      return failProfile(res.status);
    } catch (err) {
      return failProfile(statusOf(err));
    }
  }, [failProfile]);

  /** Check if the current user has a given role */
  const hasRole = useCallback(
    (role: string): boolean => {
      return info?.roles?.includes(role) ?? false;
    },
    [info]
  );

  /**
   * Logout: end the brewauth session.
   *
   * There is no server-side "logoff" call any more (jorigin/brewauth#99):
   * the Java `POST api/auth/logoff` endpoint it used to hit was a no-op once
   * brewauth owned sessions and token revocation. brewauth's own
   * `end_session`, via `oidcLogout` below, is the sole sign-out.
   */
  /**
   * Drop this tab's auth state after a sign-out elsewhere or a failed one
   * here: close an open re-auth prompt (it would sit over the signed-out
   * panel, offering to sign back in to a session that was just ended), clear
   * this tab's per-user storage (sessionStorage is per tab), and drop the
   * tokens: a tab showing the signed-out panel must hold none, or its
   * background requests and renewals carry on behind it (#2272). The stored
   * user is shared, so removing it here is often a repeat; it is still the
   * only guarantee for this tab.
   *
   * `panel` says which signed-out panel to show. It is set in the same
   * synchronous block as authenticated=false, before any await: a frame with
   * authenticated=false and no panel flag is one where ProtectedRoute
   * auto-logs in onto a still-live SSO session (#2272).
   */
  const signOutLocally = useCallback(async (panel: 'remote' | 'local') => {
    profileKnown.current = false;
    setUserProfile(null);
    setAuthenticated(false);
    if (panel === 'remote') setRemotelySignedOut(true);
    else setLocalSignout(true);
    auth.reauth.cancel();
    clearUserStorage();
    userManager.stopSilentRenew();
    await userManager.removeUser().catch(() => {});
  }, []);

  const handleLogout = useCallback(async () => {
    // Do NOT clear local auth state here: signoutRedirect() navigates the
    // whole browser away to brewauth's end_session (which revokes the SSO
    // session and returns to post_logout_redirect_uri). Flipping
    // authenticated=false first would trip ProtectedRoute's
    // `!authenticated -> login()` effect, whose signinRedirect races and
    // beats this signout, silently re-logging the user back in via the
    // still-live SSO session.
    //
    // Per-user caches (brewDB tabs, brew-ai transcripts) are not auth state
    // and go now. A request the re-auth prompt just rejected can still write
    // the brewDB store back before the page leaves, so clear again as it
    // unloads. Only an explicit logout runs this: resuming a timed-out
    // session by signing in again keeps the resume cache (#2272).
    clearUserStorage();
    window.addEventListener('pagehide', clearUserStorage, { once: true });
    // The session being signed out, read before signoutRedirect removes it:
    // its auth_time is what a later sign-in must beat (#2272).
    const signingOut = await userManager.getUser().catch(() => null);
    try {
      await oidcLogout();
    } catch (err) {
      // First: persist the pending sign-out, so a reload, a new tab or an
      // open one cannot auto-login over the live SSO session (#2272).
      markLocalSignoutPending(authTimeOf(signingOut));
      // Metadata fetch or redirect setup failed, so end_session never ran
      // and the SSO session may still be live. Reloading the app here used
      // to let ProtectedRoute's auto-login ride that session straight back
      // in (#2192). Fail safe instead: drop the stored user (signoutRedirect
      // may have failed before removing it) and sign this tab out locally.
      // localSignout lands in the same render as authenticated=false, so
      // ProtectedRoute never sees one without the other; it then shows a
      // signed-out panel, never an automatic sign-in.
      console.error('brewauth signout failed:', err);
      await signOutLocally('local');
      // Try to land on brewauth's sign-in page; it asks for credentials even
      // over a live SSO session. If brewauth is unreachable this fails too
      // and the panel's Sign in button retries it.
      void loginFresh().catch(() => {});
    }
  }, [signOutLocally]);

  // Initialize on mount: ask the server who we are. Auth is a Bearer held in
  // memory (not a cookie), so a 200 from /api/profiles/self — with the Bearer
  // attached by apiClient — is how we know we're logged in after a refresh.
  useEffect(() => {
    async function init() {
      // A sign-out is still pending (#2272): try its end_session once more
      // before showing the signed-out panel. When brewauth answers, the page
      // leaves for it and the post-logout landing clears the marker.
      if (
        isLocalSignoutPending() &&
        !signoutRetriedThisLoad &&
        takeCappedAttempt(SIGNOUT_RETRY_KEY, SIGNOUT_RETRY_MAX, SIGNOUT_RETRY_WINDOW_MS)
      ) {
        signoutRetriedThisLoad = true;
        await Promise.race([
          oidcLogout().catch(() => {}),
          new Promise((resolve) => setTimeout(resolve, SIGNOUT_RETRY_WAIT_MS)),
        ]);
      }
      await refreshProfile();
      setLoading(false);
    }
    // fire-and-forget: init() cannot reject; refreshProfile() catches into failProfile() and the sign-out retry has its own .catch
    void init();
  }, [refreshProfile]);

  // Cross-tab session coordination lives outside React (a shared-token timer +
  // a window 'storage' listener), so it must be wired via an effect — there is
  // no derived-state or event-handler equivalent for "another tab changed the
  // shared auth store". onRemoteSignout drops local auth state; ProtectedRoute
  // then routes to brewauth, where the already-revoked SSO session shows login.
  useEffect(() => {
    const unregister = setRemoteSignoutHandler(() => {
      void signOutLocally('remote');
    });
    // Another tab's sign-out failed (#2272): sign out here too, the same way,
    // and never auto-login. The marker going away (a credentialed sign-in or
    // a completed end_session elsewhere) lifts the restriction.
    const onMarker = (e: StorageEvent) => {
      if (e.key !== LOCAL_SIGNOUT_KEY && e.key !== null) return;
      if (isLocalSignoutPending()) {
        void signOutLocally('local');
      } else {
        setLocalSignout(false);
      }
    };
    window.addEventListener('storage', onMarker);
    // No `userLoaded` listener: a silent renew raises it too, so a pending
    // sign-out ends only at the redirect sign-in callback (authRedirects.ts).
    // The re-auth pop-up does ask for credentials (prompt=login), but it
    // carries no fresh-sign-in nonce, so it leaves the marker set; the
    // signed-out panel's Sign in (loginFresh) is the way out (#2272).
    const cleanup = auth.initCrossTabSession();
    const stopActivity = startActivityTracking(() => auth.notifyActivity());
    return () => {
      stopActivity();
      cleanup();
      unregister();
      window.removeEventListener('storage', onMarker);
    };
  }, [signOutLocally]);

  const value = useMemo<AuthContextType>(
    () => ({
      loading,
      userProfile,
      authenticated,
      hasRole,
      handleLogout,
      refreshProfile,
      remotelySignedOut,
      localSignout,
      unavailable,
    }),
    [loading, userProfile, authenticated, hasRole, handleLogout, refreshProfile, remotelySignedOut, localSignout, unavailable],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
